/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { Editor, Transforms, BaseSelection, Element, Descendant, BaseRange, Text } from "slate";
import { jsx } from "slate-hyperscript";

import { forEachNodeInSelection } from "src/components/ViennaEditor/lib/helpers";

/**
 * This function creates a new list at the current selection
 * Can either be a bulleted list or a numbered list
 */
export const createList = (
  editor: Editor,
  listType: "bulleted-list" | "numbered-list",
  startingAt: number = 1
): void => {
  const { selection } = editor;
  // edges are the start and end point of a location, here the selection
  const edges = Editor.edges(editor, Editor.path(editor, selection!));
  // replace the hyphen and space with empty string
  Transforms.insertText(editor, "", { at: Editor.range(editor, edges[0], edges[1]) });
  // create a new list item
  const bullet = jsx("element", { type: "list-item" }, { text: "" });
  // create a new list containing the new list item
  const list = jsx("element", { type: listType, start: startingAt }, bullet);
  // insert the new list into the editor
  const fragment = jsx("fragment", {}, list) as Descendant[];
  Transforms.insertFragment(editor, fragment, { at: edges[0].path });
  // set the selection to the new list item
  Transforms.select(editor, edges[0]);
};

/**
 * this wraps a node in the appropriate list type, indenting the list
 */
const wrapNode = (editor: Editor, type: string | undefined, selection: BaseRange) => {
  if (type === "bulleted-list") {
    // if it is a bulleted list, insert a new bullet pbulletedList(editor, true, selection);
    Transforms.wrapNodes(editor, { type: "bulleted-list", children: [] }, { at: selection });
    return;
  }
  if (type === "numbered-list") {
    // if it is a numbered list, insert a new numbered list point
    Transforms.wrapNodes(editor, { type: "numbered-list", start: 1, children: [] }, { at: selection });
    return;
  }
  if (type === "list-item") {
    // if it is a list item, we must get the type of the list that the list item is in
    const list = Editor.above(editor, { at: selection.anchor.path.slice(0, -1) });
    if (list) {
      Transforms.wrapNodes(
        editor,
        { type: (list[0] as Element).type, start: (list[0] as Element).start, children: [] },
        { at: selection }
      );
    }
    return;
  }
  if (type === "plain") {
    // if we are looking at a selection of plain text, insert a tab at the start of the line
    Transforms.insertText(editor, "\t", { at: { path: selection.anchor.path, offset: 0 } });
    return;
  }
  // if the selection is collapsed and we are not in a list, just insert a tab character
  Transforms.insertText(editor, "\t", { at: selection });
};

/**
 *  This function indents the lists in the current selection
 */
export const indentLists = (editor: Editor): void => {
  if (editor.selection) {
    // Loop through each node and wrap it in the appropriate list type
    forEachNodeInSelection(editor, editor.selection, (selection: BaseRange, type: string | undefined) => {
      wrapNode(editor, type, selection);
    });
  }
};

/**
 * This function lifts a list item up the node tree, outdenting it.
 * If the selection is a list, not a list item, it will call the outdentLists function on that list
 */
const liftList = (editor: Editor, type: string | undefined, selection: BaseSelection) => {
  if (selection) {
    // if the node is a list we want to lift it
    const path = selection.anchor.path.slice(0, -1);
    if (type === "list-item") {
      // if the path has a length greater than 1, we will just lift the list (i.e. move it up one level)
      if (path.length > 2) {
        Transforms.liftNodes(editor, { at: selection });
        return;
      }
      // otherwise, we will delete the list entirely
      deleteListItem(editor, selection);
      return;
    }
    if (type === "bulleted-list" || type === "numbered-list") {
      // if the node is a bulleted or numbered list, we call the outdentLists on this selection
      outdentLists(editor, selection);
      return;
    }
    if (!type || type === "plain") {
      // if the type is either plain or non-existent, we are looking at text
      const node = Editor.node(editor, selection)[0];
      // check if the first character is a tab
      if ((node as Text).text[0] === "\t") {
        // if it is a tab, delete it
        Transforms.delete(editor, { at: { path: selection.anchor.path, offset: 0 } });
      }
      return;
    }
  }
};

/**
 * This function outdents the lists in the current selection
 */
export const outdentLists = (editor: Editor, selection: BaseSelection): void => {
  if (selection) {
    // loop through each node in the selection and lift all the lists
    forEachNodeInSelection(
      editor,
      selection,
      (currentSelection: BaseRange, type: string | undefined) => {
        liftList(editor, type, currentSelection);
      },
      true
    );
  }
};

/**
 * This function deletes a list item entirely, no matter the depth
 */
export const deleteListItem = (editor: Editor, selection: BaseSelection): void => {
  if (selection) {
    // if we are in a nested list, outdent the list, then recursively call this function
    if (selection.anchor.path.length > 3) {
      // this should always be true, unless something else is wrong
      // have this check to prevent errors just in case
      if ((Editor.node(editor, selection.anchor.path.slice(0, -1))[0] as Element).type === "list-item") {
        outdentLists(editor, selection);
        deleteListItem(editor, editor.selection);
      }
      return;
    }
    const path = selection.anchor.path.slice(0, 2);
    const node = (Editor.node(editor, path)[0] as Element).children;
    // case 1: we are at the top level of the list
    if (path[1] === 0) {
      try {
        // try to split the list into two lists, and delete the first one
        Transforms.splitNodes(editor, { at: [path[0], 1] });
        Transforms.delete(editor, { at: [path[0]] });
      } catch (e) {
        // if this fails, this must be the only list item, so we can just delete the list
        Transforms.delete(editor, { at: [path[0]] });
      }
      // insert the text back into the editor
      Transforms.insertNodes(editor, { type: "plain", children: node }, { at: [path[0]] });
      // set the selection
      Transforms.select(editor, path);
      return;
    }
    // case 2: we are in the middle or end of the list

    // remove the list item
    Transforms.removeNodes(editor, { at: selection });

    try {
      // try to split the list into two lists, and insert the text between the two lists
      Transforms.splitNodes(editor, { at: path });
      Transforms.insertNodes(editor, { type: "plain", children: node }, { at: [path[0] + 1] });
    } catch (e) {
      // if this fails, we must be at the end of the list, so we can just insert the text after it
      Transforms.insertNodes(editor, { type: "plain", children: node }, { at: [path[0] + 1] });
    }
    // set the selection
    Transforms.select(editor, [path[0] + 1, 0]);
  }
};
